import { useWorkspaceText } from '../interface-copy';
import { Search, ShieldCheck } from 'lucide-react';
import type { NativePostgresTable, NativePostgresTestResult } from './model';
import './table-picker.css';
import { useNamespaceCopy } from '@/i18n/ns';
import nativeCopyCatalog from '@/i18n/ns/native-postgres';

type Table = NativePostgresTable & { schema: string };
const resource = (table: Table) => `${table.schema}.${table.name}`;

export function TablePicker({
  tables,
  selected,
  query,
  checks,
  onQuery,
  onToggle,
  onSelect,
  disabled,
}: {
  tables: Table[];
  selected: string[];
  query: string;
  checks?: NativePostgresTestResult['checks'];
  onQuery: (value: string) => void;
  onToggle: (resource: string) => void;
  onSelect: (resources: string[], selected: boolean) => void;
  disabled: boolean;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const copy = useNamespaceCopy(nativeCopyCatalog);
  const visible = tables.filter((t) => resource(t).toLowerCase().includes(query.toLowerCase()));
  const schemas = [...new Set(visible.map((t) => t.schema))];
  return (
    <div className="ws-pg-table-picker">
      <div className="ws-pg-checks">
        <ShieldCheck size={14} /> {uiText('TLS verified')} ·{' '}
        {checks?.readOnly === 'replica'
          ? copy.tlsReplica
          : checks?.readOnly === 'role'
            ? copy.tlsAccount
            : copy.tlsChecked}
      </div>
      <label className="ws-pg-search">
        <Search size={16} />
        <input
          type="search"
          value={query}
          onChange={(event) => onQuery(event.currentTarget.value)}
          placeholder={uiText('Search schemas and tables')}
          aria-label={uiText('Search tables')}
        />
      </label>
      <div className="ws-pg-selection-bar">
        <span>
          {copy.tablesSelected
            .replace('{count}', String(selected.length))
            .replace('{total}', String(tables.length))}
        </span>
        <button
          type="button"
          className="ws-text-button"
          disabled={disabled}
          onClick={() => onSelect(visible.map(resource), true)}
        >
          {query ? uiText('Select matching tables') : uiText('Select all tables')}
        </button>
        <button
          type="button"
          className="ws-text-button"
          disabled={disabled || !selected.length}
          onClick={() => onSelect(tables.map(resource), false)}
        >
          {uiText('Clear')}
        </button>
      </div>
      <p className="ws-pg-selection-note">
        {uiText('Select a whole schema or individual tables. New tables need a fresh selection.')}
      </p>
      <div className="ws-pg-schema-list">
        {!visible.length && (
          <p className="ws-pg-selection-note">{uiText('No tables match this search.')}</p>
        )}
        {schemas.map((schema) => {
          const all = tables.filter((t) => t.schema === schema);
          const count = all.filter((t) => selected.includes(resource(t))).length;
          return (
            <section className="ws-pg-schema" key={schema}>
              <label className="ws-pg-schema-heading">
                <input
                  type="checkbox"
                  aria-label={uiText('All tables in {value1}', { value1: schema })}
                  checked={count === all.length}
                  ref={(node) => {
                    if (node) node.indeterminate = count > 0 && count < all.length;
                  }}
                  disabled={disabled}
                  onChange={() => onSelect(all.map(resource), count !== all.length)}
                />
                <strong>{schema}</strong>
                <span>
                  {copy.tablesInSchema
                    .replace('{count}', String(count))
                    .replace('{total}', String(all.length))}
                </span>
              </label>
              {visible
                .filter((t) => t.schema === schema)
                .map((table) => (
                  <label className="ws-pg-table" key={resource(table)}>
                    <input
                      type="checkbox"
                      checked={selected.includes(resource(table))}
                      disabled={disabled}
                      onChange={() => onToggle(resource(table))}
                    />
                    <span>
                      <strong>{resource(table)}</strong>
                      <small>{table.columns.join(' · ')}</small>
                    </span>
                    <small className="ws-pg-table-mode">
                      {table.write ? uiText('Read & write available') : uiText('Read only')}
                    </small>
                  </label>
                ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}
